// Контракт модуля StudyHub — IA-агностичное описание способностей.
//
// Манифест отвечает на вопрос «что модуль умеет», а НЕ «где он лежит в меню».
// Поверхности (сайдбар, launcher, Cmd-K, панель Темы, воркспейс курса) читают
// реестр и применяют собственные правила отбора по `kind` + `learning.role`.
// Смена IA = перерисовка потребителя, а не правка 20 манифестов.
//
// ┌─ ДЕКРЕТ (ТЗ §4.2, закрепляется негативным coverage-тестом) ────────────────┐
// │ В `ModuleManifest` ЗАПРЕЩЕНО заводить поля расположения в интерфейсе:      │
// │   `nav.group`, `bottomNavSlot`, `homeLauncher`, `cmdkActions: boolean`,    │
// │   `workspaceTab`, `hasTrpc`.                                              │
// │                                                                           │
// │ Почему это не придирка: как только манифест начинает описывать «я вторая  │
// │ плитка на Главной», редизайн навигации превращается в правку всех         │
// │ манифестов, а модуль перестаёт быть переносимым между поверхностями.      │
// │ Вместо `workspaceTab` — `learning.scopes ∋ "material"`; вместо `hasTrpc`  │
// │ — факт наличия `contracts/<id>/router.ts` (проверяет coverage-тест №6).   │
// │ Если следующему агенту «нужно поле для меню» — нужно не поле, а правило   │
// │ отбора в потребителе.                                                     │
// └───────────────────────────────────────────────────────────────────────────┘

import type { EventName } from "../analytics/events.js";
import { QUOTA_PERIODS, type QuotaKey, type QuotaPeriod } from "../billing/tier-limits.js";
import type { SubscriptionTier } from "../constants.js";
import type { KillswitchOp } from "../contracts/admin/types.js";
import type { CoverageKind } from "../contracts/learning/schemas.js";
import type { BriefCardKind, RecommendationItemType } from "../contracts/recommendations/types.js";
import type { SearchableType } from "../contracts/search/schemas.js";
import type { TrashEntityType } from "../contracts/trash/schemas.js";
import type { TutorArtifactKind } from "../contracts/tutor/schemas.js";
import type { ArtifactKind as NotificationArtifactKind } from "../utils/artifact-group-key.js";
import type { ModuleId } from "./ids.js";
import type { ModuleLearningRole, ModuleScope, TopicGenerationMode } from "./roles.js";

/**
 * Природа модуля — определяет применимые правила, а не место в меню.
 *
 * Именно по `kind` поверхности решают, кого показывать: панель Темы берёт
 * `learning-format`, launcher — `standalone-tool` + `capture`, сайдбар — `meta`.
 */
export const MODULE_KINDS = [
	/** Формат изучения темы: notes, quizzes, flashcards, glossary, audio-recap, voice-tutor. */
	"learning-format",
	/** Приносит материал, но не изучает тему: recordings, discovery. */
	"capture",
	/** Свой цикл и свой вход: essays, visual-explainer. */
	"standalone-tool",
	/** Сквозная поверхность: tutor. */
	"platform-capability",
	/** Каркас продукта: materials, library, courses, calendar, streaks, course-templates. */
	"meta",
	/** Считается из чужих данных: progress, weak-spots. */
	"derivative",
	/** Жизненный цикл данных: trash. */
	"lifecycle",
	/** Федеративный сервис за `ExternalModulePort`: characters. */
	"external-module",
] as const;
export type ModuleKind = (typeof MODULE_KINDS)[number];

/**
 * Стадия жизненного цикла модуля.
 *
 * - `ga`                  — общедоступен, дефолт rollout = on;
 * - `beta`                — работает, но раскатывается таргетированно (дефолт для внешних);
 * - `deprecated`          — сворачивается: данные видны, новое создание закрыто
 *                           (резолвер отдаёт `read_only` / `lifecycle_removed`);
 * - `disabled-by-default` — существует в коде, но выключен, пока админ не включит
 *                           явно (дефолт rollout = off).
 */
export const MODULE_LIFECYCLES = ["ga", "beta", "deprecated", "disabled-by-default"] as const;
export type ModuleLifecycle = (typeof MODULE_LIFECYCLES)[number];

/**
 * Целевая платформа сборки.
 *
 * ⚠️ Это НЕ `contracts/_shared/locale.ts::Platform` (`web|android|ios|admin`).
 * Тот тип описывает клиента для push/аналитики, здесь же — площадка, на которой
 * модуль физически работоспособен: `capacitor` покрывает и Android, и iOS
 * (лок-скрин-запись есть на обеих), `admin` продуктовым модулям не адресуется.
 * Мост между осями — `toModulePlatform` в `availability.ts`.
 */
export const MODULE_PLATFORMS = ["web", "capacitor"] as const;
export type ModulePlatform = (typeof MODULE_PLATFORMS)[number];

/**
 * Артефакт, который производит модуль — ключ ко всем сквозным реестрам
 * (корзина, поиск, уведомления, `createFromChat`, рекомендации).
 *
 * Состав НЕ изобретается заново, а собирается из существующих союзов:
 *   - `NotificationArtifactKind` (10) — `utils/artifact-group-key.ts`, канон
 *     ключа агрегации уведомлений: material, notes, flashcards, quiz, recording,
 *     audio-recap, essay, visual, glossary, study-plan;
 *   - `TutorArtifactKind` (2) — `contracts/tutor/schemas.ts`: diagram, infographic
 *     (артефакты чата, миграция 0087);
 *   - `"voice-plan"` — единственный ДОБАВЛЕННЫЙ литерал: он есть в `COVERAGE_KINDS`
 *     и в `TOPIC_KIT_KINDS`, но ни в одном союзе артефактов его нет.
 *
 * ⚠️ Известный разрыв именования, который обязан свести Ш2: уведомления зовут
 * конспект `notes`, а `COVERAGE_KINDS`/`TOPIC_KIT_KINDS` — `note`. Пока это
 * разные ключи разных реестров; маппер обязан быть ровно один и явный.
 */
export type ModuleArtifactKind = NotificationArtifactKind | TutorArtifactKind | "voice-plan";

/** Идентификатор внешнего (федеративного) сервиса. */
export const EXTERNAL_SERVICE_IDS = ["characterhub"] as const;
export type ExternalServiceId = (typeof EXTERNAL_SERVICE_IDS)[number];

/**
 * Имя FC-инструмента репетитора (`modules/tutor/_internal/tools/registry.ts`).
 *
 * Намеренно `string`, а не union из 32 литералов. Причина: реестр инструментов
 * живёт в `apps/server`, а `packages/shared` его импортировать не может
 * (лист графа). Скопировать список сюда — значит завести вторую копию, которая
 * будет расходиться ровно так же, как расходятся сегодня два объявления
 * `KILLSWITCH_OPS` (§2.8 п.9) — известный дефект, тиражировать который нельзя.
 *
 * Гарантия корректности — coverage-тест на СЕРВЕРЕ: каждое имя из
 * `contributes.tutorActions.{reads,writes}` обязано существовать в registry,
 * и каждый из 32 инструментов обязан иметь владельца-модуль.
 */
export type FcName = string;

/** Единица потребления квоты (нигде больше не зафиксирована — новая разметка). */
export const QUOTA_UNITS = ["call", "minute", "byte"] as const;
export type QuotaUnit = (typeof QUOTA_UNITS)[number];

/**
 * Одна квота модуля.
 *
 * ⚠️ Поля `period` здесь НЕТ — сознательное отступление от текста ТЗ §4.2.
 * Период уже задан исчерпывающей картой `QUOTA_PERIODS: Record<QuotaKey, QuotaPeriod>`
 * (`billing/tier-limits.ts`). Продублировать его в манифесте = завести второй
 * источник правды об одном факте, то есть ровно тот дрейф, ради устранения
 * которого строится платформа. Читать период — `quotaPeriodOf(key)`.
 */
export interface ModuleQuota {
	key: QuotaKey;
	unit: QuotaUnit;
}

/** Период сброса квоты — производный от канонической карты `QUOTA_PERIODS`. */
export function quotaPeriodOf(key: QuotaKey): QuotaPeriod {
	return QUOTA_PERIODS[key];
}

/** Вклады модуля в сквозные реестры (ТЗ §5 — 13 реестров). */
export interface ModuleContributions {
	/** Типы корзины. Пустой массив = осознанный opt-out, требует записи в baseline. */
	trashEntities?: TrashEntityType[];
	/** Индексы Meilisearch, участвующие в глобальном поиске. */
	searchIndices?: SearchableType[];
	/** Синкаемые таблицы → все 4 точки PowerSync (publication / sync-rules / schema / writeHandlers). */
	syncTables?: string[];
	/** Таблицы, для которых модуль объявляет CASL-writeHandler. */
	writeHandlers?: string[];
	/** Имена шагов `processAccountDeletion`, стирающих данные модуля. */
	deletionSteps?: string[];
	/** Имена Inngest-функций, регистрируемых в `createAllFunctions`. */
	inngestFunctions?: string[];
	/**
	 * Идентификаторы Novu-воркфлоу. `string`, а не `NovuWorkflowId`, по той же
	 * причине, что и `FcName`: перечисление живёт в `apps/server`.
	 */
	novuWorkflows?: string[];
	recommendationTypes?: RecommendationItemType[];
	briefKinds?: BriefCardKind[];
	/** События каталога аналитики (106 шт.) — строго типизировано, не `string[]`. */
	analyticsEvents?: EventName[];
	/** FC-инструменты репетитора: чтение и запись разделены ради `read_only`-деградации. */
	tutorActions?: { reads?: FcName[]; writes?: FcName[] };
	/** Виды артефактов, создаваемых из чата (меню «создать»). */
	createFromChat?: ModuleArtifactKind[];
	/** Префиксы роутов — вход Route Guard и coverage-теста «роут ↔ манифест». */
	routes?: string[];
	/** Префиксы, на которых приложение рисует overlay-каркас (без сайдбара). */
	overlayShellPrefixes?: string[];
}

/** Способности обучающего формата (читают панель Темы, coverage, скоуп-селектор, тьютор). */
export interface ModuleLearningCapability {
	/** Шаг спины темы ①②③. */
	role: ModuleLearningRole;
	/** Где модуль умеет генерировать/работать. */
	scopes: ModuleScope[];
	/** Производимый артефакт; `null` — модуль работает, но артефакта не создаёт. */
	artifactKind: ModuleArtifactKind | null;
	/**
	 * Участие в панели Темы. Поглощает `TOPIC_KIT_KINDS` / `COVERAGE_KINDS` /
	 * `RECOMMEND_ORDER` / `DEFAULT_MINUTES` (Ш2 делает их производными).
	 * Отсутствие блока = формат в теме не участвует (например `essays`).
	 */
	topic?: {
		generation: TopicGenerationMode;
		/** Порядок в комплекте / панели / промпте `generateStudyPlan`. */
		order: number;
		/**
		 * Порядок в лестнице «что делать дальше» (`RECOMMEND_ORDER` в
		 * `learning.coverage.get`).
		 *
		 * Отдельное поле, а не переиспользование `order`, потому что это ДРУГОЙ
		 * порядок: показываем конспект → термины → карточки → тест (порядок
		 * изучения), а рекомендуем конспект → карточки → тест → термины (порядок
		 * пользы следующего шага). Свести их в одно число нельзя, не сломав одну
		 * из двух поверхностей; вывести второй из `role` — тоже (внутри `encode`
		 * конспект и термины стоят по-разному в двух списках).
		 *
		 * Скаляр на модуль, а не список: седьмого перечисления форматов из этого
		 * поля не получится, потому что перечислять нечего.
		 */
		recommendOrder: number;
		/** Оценка минут для панели, когда у активности нет собственной. */
		defaultMinutes: number;
		/**
		 * Ключ формата — он же значение DB-enum `study_activity_type`, он же
		 * элемент `COVERAGE_KINDS`. ОДИН ключ на три реестра, а не три имени.
		 *
		 * Тип сужен до `CoverageKind` (Ш2): изначально стоял `string` из
		 * опасения, что enum живёт в `apps/server` и союз недоступен. Это
		 * оказалось не так — `COVERAGE_KINDS` объявлен в контракте `learning`,
		 * а enum БД содержит его целиком плюс легаси-хвост
		 * (`material`/`chat`/`visual-explainer`/`voice-tutor`), который форматом
		 * темы не является. Сужение даёт компилятору ловить опечатку в
		 * манифесте, а соответствие enum'у по-прежнему сверяет coverage-тест №11.
		 *
		 * ⚠️ НЕ путать с `artifactKind`: тот — ключ агрегации уведомлений и
		 * корзины (`notes`, мн. ч.), этот — ключ покрытия темы (`note`). Разрыв
		 * именования реален, и манифест — единственное место, где эти два ключа
		 * стоят рядом. Это и есть тот самый «ровно один явный маппер».
		 */
		activityType?: CoverageKind;
	};
}

/** Зависимости модуля. */
export interface ModuleRequirements {
	/**
	 * Функциональные зависимости: без них часть возможностей модуля не работает
	 * (пример: `export_glossary_to_flashcards` требует и `glossary`, и `flashcards`).
	 */
	modules?: ModuleId[];
	/**
	 * Каркасные зависимости: модуль живёт ВНУТРИ мета-поверхности и без неё не
	 * имеет входа (`courses` / `library` / `calendar`).
	 *
	 * Для резолвера обе разновидности одинаково жёсткие — недоступная зависимость
	 * даёт `dependency_missing`. Разделение нужно потребителям: `meta` объясняется
	 * пользователю как «раздел недоступен», `modules` — как «функция недоступна»,
	 * и coverage-тесты по-разному проверяют эти рёбра.
	 */
	meta?: ModuleId[];
	/** Внешний сервис — только для `kind: "external-module"`. */
	external?: ExternalServiceId;
}

/** Манифест модуля — единственное описание, из которого выводятся 13 реестров. */
export interface ModuleManifest {
	id: ModuleId;
	kind: ModuleKind;
	lifecycle: ModuleLifecycle;

	/** Способности формата обучения; отсутствует у `meta` / `lifecycle` / `derivative`. */
	learning?: ModuleLearningCapability;

	requires?: ModuleRequirements;

	/**
	 * Квоты модуля. Модуль может иметь несколько с разными периодами —
	 * прецедент `discovery`: `discovery_fast_daily` + `discovery_deep_monthly`.
	 */
	quotas?: ModuleQuota[];

	/**
	 * Op аварийного выключателя из существующих 15 (`KILLSWITCH_OPS`).
	 * Полярность ops неизменна: `enabled = true` означает «убито».
	 * Заводить `tool.*`-ops запрещено (анти-цель ТЗ §15): killswitch — инструмент
	 * аварии, продуктовое включение — это `ModuleRollout`.
	 */
	killswitchOp?: KillswitchOp;

	/**
	 * Минимальный тариф. ЕДИНСТВЕННЫЙ источник состояния `teaser`
	 * (декрет пользователя от 2026-08-09, ТЗ §2.8 п.6).
	 * Квоты отвечают только за объём потребления и тарифным гейтом не являются —
	 * «квота 0 на Free» как способ закрыть фичу запрещён.
	 */
	minTier?: SubscriptionTier;

	/** Платформы, где модуль работоспособен. Отсутствие поля = обе. */
	platforms?: ModulePlatform[];

	contributes: ModuleContributions;
}
